import React from 'react'
import { FaStar, FaRegStar, FaRegAngry, FaRegFrown, FaRegMeh, FaRegSmile, FaRegGrin, FaRegGrinStars } from 'react-icons/fa'
import { Box, Button, Icon, Stack, Text } from '@/components/lib'
import { ratingLabels, ratingPoints, ratingSymbol } from '@/utils/utils'

// The faces a smiley scale draws from, saddest first. A scale shorter than six
// points spreads across this ramp rather than taking the first n, so a
// three-point scale reads sad / neutral / happy instead of sad / sadder /
// neutral, and every odd-length scale keeps a neutral face in the middle.
// Spelled out per length rather than computed, because the participant app has
// to pick exactly the same faces and an arithmetic spread rounds differently.
const SMILEY_RAMP = [FaRegAngry, FaRegFrown, FaRegMeh, FaRegSmile, FaRegGrin, FaRegGrinStars]
const SMILEY_INDICES = {
  3: [0, 2, 4],
  4: [0, 1, 3, 4],
  5: [0, 1, 2, 3, 4],
  6: [0, 1, 2, 3, 4, 5]
}

// Comfortably above the 44px minimum tap target, which is what caps how many
// points fit across a phone — and why a six-point scale drops its per-point
// labels rather than shrinking them.
const SYMBOL_SIZE = '2.25rem'

export default function Rating ({ dataType, value, onChange, isDisabled }) {
  // These fall back rather than returning nothing, so there is always a scale to
  // draw: a field whose configuration went astray renders as a five-point star
  // scale instead of as an empty row.
  const points = ratingPoints(dataType)
  const labels = ratingLabels(dataType)
  const symbol = ratingSymbol(dataType)

  // An answer left over from a scale that has since been shortened reads as
  // nothing selected. Deliberately *not* reported through `onChange`: this
  // renders during mount, and in the response editor `onChange` is a user-edit
  // signal with side effects — clearing the last pending change there deletes
  // the participant's saved draft from the server. Opening a response must not
  // do that. Nothing is lost by staying quiet: the editor submits only the
  // fields staff actually changed, so an untouched stale value is left alone.
  const rating = Number.isInteger(value) ? value : parseInt(value, 10)
  const selected = Number.isInteger(rating) && rating >= 1 && rating <= points ? rating : null

  // Six labelled columns don't fit a phone, and a half-truncated label reads
  // worse than none — so per-point labels appear only where there is room, and
  // the endpoints carry the meaning everywhere else. This decides labelling
  // only: the columns themselves stretch equally at every point count, so a
  // six-point scale spreads across the row like a five-point one rather than
  // bunching at the left.
  const showsEveryLabel = points <= 5
  const labelDisplay = showsEveryLabel ? { base: 'none', md: 'block' } : 'none'
  const endpointDisplay = showsEveryLabel ? { base: 'flex', md: 'none' } : 'flex'

  const symbolFor = index => {
    if (symbol === 'smileys') return SMILEY_RAMP[(SMILEY_INDICES[points] ?? SMILEY_INDICES[5])[index]]
    // Stars fill cumulatively — every star up to the one chosen — which is the
    // convention everywhere else and reads as broken if it isn't followed.
    if (symbol === 'stars') return selected !== null && index < selected ? FaStar : FaRegStar
    return null
  }

  // Stars are gold, the other two symbols take the brand colour: a filled star
  // that isn't amber doesn't read as filled, whereas a smiley or a number is
  // marked by being highlighted at all.
  const activeColour = symbol === 'stars' ? 'rating.filled' : 'primary.solid'

  const select = point => {
    if (isDisabled) return
    onChange(point === selected ? null : point)
  }

  return (
    <Stack gap={3}>
      <Stack direction='row' gap={1} flexWrap='wrap' alignItems='flex-start'>
        {labels.map((label, index) => {
          const point = index + 1
          const isSelected = selected === point
          // A star is "on" as soon as the rating reaches it; the other two
          // symbols mark only the point actually chosen.
          const isActive = symbol === 'stars' ? selected !== null && point <= selected : isSelected
          const SymbolIcon = symbolFor(index)
          return (
            <Stack key={point} gap={1} alignItems='center' flex='1 1 0' minWidth={SYMBOL_SIZE}>
              <Box
                as='button'
                type='button'
                aria-label={`${point} — ${label}`}
                aria-pressed={isSelected}
                disabled={isDisabled}
                onClick={() => select(point)}
                display='flex'
                alignItems='center'
                justifyContent='center'
                width={SYMBOL_SIZE}
                height={SYMBOL_SIZE}
                rounded='md'
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.6 : 1}
                borderWidth={SymbolIcon ? 0 : '1px'}
                borderColor={isSelected ? 'border.primary' : 'border.default'}
                bg={SymbolIcon ? undefined : (isSelected ? 'primary.solid' : 'bg.surface')}
                color={SymbolIcon ? (isActive ? activeColour : 'fg.subtle') : (isSelected ? 'primary.contrast' : 'fg.default')}
                _hover={isDisabled ? undefined : { bg: SymbolIcon ? 'bg.subtle' : (isSelected ? 'primary.solid' : 'bg.subtle') }}
              >
                {SymbolIcon
                  ? <Icon as={SymbolIcon} boxSize='1.5rem' />
                  : <Text fontSize='sm' fontWeight='bold'>{point}</Text>}
              </Box>
              <Text display={labelDisplay} fontSize='xs' color='fg.muted' textAlign='center' lineHeight='short'>{label}</Text>
            </Stack>
          )
        })}
      </Stack>

      <Stack direction='row' justifyContent='space-between' alignItems='center' gap={3} pt={2} display={endpointDisplay}>
        <Text fontSize='xs' color='fg.muted'>{labels[0]}</Text>
        <Text fontSize='xs' color='fg.muted'>{labels[labels.length - 1]}</Text>
      </Stack>

      <Stack direction='row' justifyContent='space-between' alignItems='center' gap={3} pt={1}>
        <Text fontSize='sm' color={selected === null ? 'fg.subtle' : 'fg.default'}>
          {selected === null ? `Choose a rating from 1 to ${points}` : `${selected} of ${points} (${labels[selected - 1]})`}
        </Text>
        {selected !== null && !isDisabled &&
          <Button variant='plain' size='xs' onClick={() => onChange(null)}>Clear</Button>}
      </Stack>
    </Stack>
  )
}
